/**
 * The shopper's explicit language choice, as the storefront API hears it.
 *
 * Module state rather than a React value because the API modules are plain
 * functions: whoever owns the storefront's language (the URL prefix, then the
 * saved choice - docs/i18n.md §3) calls `setStorefrontLanguageHeader(code)`,
 * and every public and account storefront request from then on carries
 * `X-Storefront-Language`. `null` sends nothing and lets the server decide from
 * the customer's preference, `Accept-Language` and the store default.
 *
 * A dependency-free leaf, like `storefrontHost.ts`, so any API module can read
 * it without an import cycle. React Query keys must still include the
 * language - this header changes the response, and a cache that ignored it
 * would serve English to a shopper who just switched to Hindi.
 */

let current: string | null = null;

export function setStorefrontLanguageHeader(code: string | null | undefined): void {
  const trimmed = code?.trim();
  current = trimmed ? trimmed : null;
}

export function getStorefrontLanguageHeader(): string | null {
  return current;
}

/** `{ 'X-Storefront-Language': code }`, or nothing when no choice was made. */
export function storefrontLanguageHeaders(): Record<string, string> {
  return current ? { 'X-Storefront-Language': current } : {};
}

/**
 * The language the server last said the live shop is answering in.
 *
 * Kept apart from the shopper's choice above: it only feeds `?lang=` on public
 * reads, so a CDN keyed on the URL can cache them (the URL must decide the
 * language for that). Sending it as the choice instead would stop the server
 * following the shopper's browser language on later visits.
 */
let resolved: string | null = null;

export function setResolvedStorefrontLanguage(code: string | null | undefined): void {
  const trimmed = code?.trim();
  resolved = trimmed ? trimmed : null;
}

/** The shopper's choice if they made one, else the language the server resolved. */
export function storefrontUrlLanguage(): string | null {
  return current ?? resolved;
}
